import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RED} from "@/shared/constants.js";
import { STEP_PATHS } from "@/modules/Quote/constants.js";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { ZIPCODE_NOT_AVAILABLE_MSG, toError } from "@/shared/utils/misc.js";
import { ilfDlfHasRequiredDefaults } from "@/modules/Quote/utils/ilfHelpers.js";
import {
  buildSubmissionRequest,
  submissionIdFromOpenOrderDto,
} from "@/modules/Quote/utils/submission.js";
import { postInsuredSubmission } from "@/modules/Quote/api/quoteApi.js";
import {
  isQuestionVisible,
  recomputeHiddenQuestionIds,
  requiredQuestionsAnswered,
} from "@/modules/Quote/api/questionsApi.js";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { RadioCard } from "@/shared/components/RadioCard.jsx";
import { Toggle } from "@/shared/components/Toggle.jsx";
import { SectionTitle } from "@/shared/components/SectionTitle.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import { btnPrimary, dis } from "@/shared/utils/styles.js";
import { useQuestionsAutoLoad } from "@/hooks/useQuestionsFetcher.js";
import { useSpecialityId } from "@/hooks/useSpeciality.js";
import { useIlfDlfResponse } from "@/hooks/useIlfDlfFetcher.js";

import { useStore } from "@/shared/store/useStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import classificationStore from "@/modules/Quote/store/classificationStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";

const PA1_KEY_BY_LABEL = {
  "diagnostic treatment only": "diagnostic",
  "or / surgical exposure": "or_assist",
  "trauma / emergency room": "er_trauma",
  "assisting in anesthesiology": "anesthesiology",
};

const EMPLOYMENT_KEY_BY_LABEL = {
  "w-2 employee": "w2",
  "independent contractor (1099)": "1099",
  "moonlighting / fill-in": "moonlight",
};

export default function ClassificationPage() {
  const navigate = useNavigate();

  // ── Store-backed reads ─────────────────────────────────────────────────
  const questionAnswers       = useStore(questionsStore, (s) => s.questionAnswers);
  const impactAnswers         = useStore(questionsStore, (s) => s.impactAnswers);
  const masterHiddenQuestionIds = useStore(questionsStore, (s) => s.masterHiddenQuestionIds);
  const visitedQuestionGroups = useStore(questionsStore, (s) => s.visitedQuestionGroups);
  const questionGroupsWithIds = useStore(questionsStore, (s) => s.questionGroupsWithIds);
  const insuredSubmissionLoading = useStore(submissionStore, (s) => s.insuredSubmissionLoading);
  const insuredSubmissionError   = useStore(submissionStore, (s) => s.insuredSubmissionError);
  const pa1Subs = useStore(classificationStore, (s) => s.pa1Subs);
  const pa2Subs = useStore(classificationStore, (s) => s.pa2Subs);
  const zip     = useStore(quoteFormStore, (s) => s.zip);
  const effDate = useStore(quoteFormStore, (s) => s.effDate);

  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  const specialityId       = useSpecialityId();
  const specialityLoadError = useStore(questionsStore, (s) => s.specialityLoadError);
  const ilfDlfResponse = useIlfDlfResponse();
  const {
    questions: questionGroups,
    loading: questionsLoading,
    error: questionsError,
  } = useQuestionsAutoLoad(specialityId);

  // Until the speciality master resolves, useQuestionsAutoLoad's fetch can't
  // run, so `questionsLoading` stays false and there'd be nothing below the
  // heading. Treat "master not yet resolved" as a loading state (or surface
  // the master fetch error) so a direct load / refresh never renders blank.
  const waitingForSpeciality = !specialityId && !specialityLoadError;
  const showLoader = questionsLoading || waitingForSpeciality;
  const loadError  = questionsError || specialityLoadError;

  // ── Visible group on this page: "About your practice" ─────────────────
  const aboutGroup =
    questionGroups.find((g) => /about\s*your\s*practice/i.test(String(g?.groupName || ""))) || null;
  const heading = aboutGroup?.groupName || "About your practice";
  useEffect(() => {
    if (aboutGroup?.groupName) questionsStore.markGroupVisited(aboutGroup.groupName);
  }, [aboutGroup?.groupName]);

  // ── canAdvance (step 2): at least one PA exposure selected AND every
  // required question on this page answered ─────────────────────────────
  const canAdvance = () =>
    (pa1Subs.length > 0 || pa2Subs.length > 0)
    && !insuredSubmissionLoading
    && requiredQuestionsAnswered(aboutGroup?.questions, questionAnswers, {
      isVisible: (q) => isQuestionVisible(q, masterHiddenQuestionIds),
    });

  // Recompute the master-tree hidden set. Simpler than the submission pages'
  // helper: one answer map, no merge. The initial set is seeded at load time
  // by useQuestionsAutoLoad (§5.1); this fires on every choice-option change.
  const recomputeMasterHidden = () => {
    questionsStore.setMasterHiddenQuestionIds(
      recomputeHiddenQuestionIds(
        questionsStore.questionGroups,
        questionsStore.questionAnswers,
        questionsStore.masterShowTargetQuestionIds,
      ),
    );
  };

  // ── Continue: create submission once, then advance ─────────────────────
  const submissionBootstrappedRef = useRef(false);
  useEffect(() => { submissionBootstrappedRef.current = false; }, [zip, effDate]);

  const handleContinue = async () => {
    if (!canAdvance()) return;

    if (!isAuthenticated) {
      submissionStore.insuredSubmissionError = null;
      submissionStore.step = 2;
      navigate(STEP_PATHS[2]);
      return;
    }
    if (submissionBootstrappedRef.current) {
      submissionStore.step = 3;
      navigate(STEP_PATHS[3]);
      return;
    }
    if (!ilfDlfHasRequiredDefaults(ilfDlfResponse)) {
      submissionStore.insuredSubmissionError = ZIPCODE_NOT_AVAILABLE_MSG;
      return;
    }

    submissionStore.insuredSubmissionError = null;
    submissionStore.insuredSubmissionLoading = true;
    try {
      const submissionReq = buildSubmissionRequest({
        zip, effDate, ilfDlfResponse,
        visitedQuestionGroups, questionGroupsWithIds,
        questionAnswers, impactAnswers,
        hiddenQuestionIds: masterHiddenQuestionIds,
      });
      const openOrder = await postInsuredSubmission(submissionReq);
      const sid = submissionIdFromOpenOrderDto(openOrder);
      if (sid) submissionStore.flowSubmissionId = sid;

      questionsStore.visitedQuestionGroups = [];
      submissionBootstrappedRef.current = true;
      signupStore.signupDone = true;
      submissionStore.step = 3;
      navigate(STEP_PATHS[3]);
    } catch (e) {
      submissionStore.insuredSubmissionError = toError(e).message;
    } finally {
      submissionStore.insuredSubmissionLoading = false;
    }
  };

  // ── Input handlers (write straight to stores) ──────────────────────────
  const setRadio = (q, optionId) => {
    questionsStore.questionAnswers = { ...questionAnswers, [String(q.id)]: String(optionId) };
    recomputeMasterHidden();
    const opt = (q.options || []).find((o) => String(o.id) === String(optionId));
    if (!opt) return;
    if (/pa classification/i.test(q.questionText)) {
      const isPA2 = /obstet/i.test(String(opt.optionValue || opt.optionLabel || ""));
      if (isPA2) {
        quoteFormStore.paClass = "PA2";
        if (pa2Subs.length === 0) classificationStore.pa2Subs = ["ob_prenatal"];
      } else {
        quoteFormStore.paClass = "PA1";
        classificationStore.pa2Subs = [];
      }
    } else if (/employment/i.test(q.questionText)) {
      const k = EMPLOYMENT_KEY_BY_LABEL[String(opt.optionLabel || "").toLowerCase()];
      if (k) quoteFormStore.employment = k;
    }
  };

  const toggleCheckbox = (q, optionId) => {
    const qid = String(q.id);
    const cur = Array.isArray(questionAnswers[qid]) ? questionAnswers[qid] : [];
    const has = cur.includes(String(optionId));
    const next = has ? cur.filter((x) => x !== String(optionId)) : [...cur, String(optionId)];
    questionsStore.questionAnswers = { ...questionAnswers, [qid]: next };
    recomputeMasterHidden();
    if (/practice exposure/i.test(q.questionText)) {
      const labels = next
        .map((id) => (q.options || []).find((o) => String(o.id) === id)?.optionLabel || "")
        .map((l) => l.toLowerCase());
      classificationStore.pa1Subs = labels.map((l) => PA1_KEY_BY_LABEL[l]).filter(Boolean);
    }
  };

  const setText = (q, value) => {
    questionsStore.questionAnswers = { ...questionAnswers, [String(q.id)]: value };
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 600, color: "#1a1a1a", margin: "8px 0 4px" }}>{heading}</h2>
      <p style={{ fontSize: 13, color: "#777", marginBottom: 16, lineHeight: 1.5 }}>Tell us about your role, exposure, and employment.</p>

      {showLoader && <Loader label="Loading questions…" />}
      {loadError && (
        <Alert type="error">Couldn't load questions. {loadError.message}</Alert>
      )}

      {aboutGroup?.questions
        ?.filter((q) => isQuestionVisible(q, masterHiddenQuestionIds))
        ?.map((q) => (
          <QuestionRow
            key={q.id}
            question={q}
            answer={questionAnswers[String(q.id)]}
            onSetRadio={setRadio}
            onToggleCheckbox={toggleCheckbox}
            onSetText={setText}
          />
        ))}

      <Spacer />
      <Alert type="error" message={insuredSubmissionError} className="alert-center" />

      <button style={dis(btnPrimary, canAdvance())} onClick={handleContinue}>
        {insuredSubmissionLoading ? "Creating order…" : (isAuthenticated ? "Create order" : "Continue")}
      </button>
    </>
  );
}

function QuestionRow({ question: q, answer, onSetRadio, onToggleCheckbox, onSetText }) {
  const opts = Array.isArray(q.options) ? [...q.options] : [];
  opts.sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0));
  const required = q.isRequired ? <span style={{ color: RED, marginLeft: 4 }}>*</span> : null;
  const isSingle = q.questionType === "RADIO_BUTTON" || q.questionType === "YES_NO";

  return (
    <div style={{ marginBottom: 12 }}>
      <SectionTitle>{q.questionText}{required}</SectionTitle>

      {isSingle && opts.map((o) => (
        <RadioCard
          key={o.id}
          selected={String(answer) === String(o.id)}
          onClick={() => onSetRadio(q, o.id)}
          title={o.optionLabel}
          subtitle={o.optionDescription || o.description || null}
        />
      ))}

      {q.questionType === "CHECKBOX" && opts.map((o) => {
        const cur = Array.isArray(answer) ? answer : [];
        return (
          <Toggle
            key={o.id}
            value={cur.includes(String(o.id))}
            onChange={() => onToggleCheckbox(q, o.id)}
            label={o.optionLabel}
            description={o.optionDescription || o.description || null}
          />
        );
      })}

      {q.questionType === "TEXTBOX" && (
        <TextInput
          value={answer || ""}
          onChange={(v) => onSetText(q, v)}
          placeholder={opts[0]?.optionLabel || ""}
        />
      )}
    </div>
  );
}
